/**
 * V82.7 Phase 1: IP 화이트리스트 관리 페이지 (system_admin 전용)
 *
 * - 등록된 IP 목록(active/inactive 구분)
 * - 신규 등록 / 비활성화 / 재활성화 / 메모 수정
 * - 감사로그 조회
 */
import React, { useMemo, useState } from 'react';
import { Plus, Power, RotateCcw, Edit2, History, X, Shield } from 'lucide-react';
import { useAuthorization } from '../../shared/hooks/useAuthorization';
import {
  useIpWhitelists,
  callCreateIpWhitelist,
  callDisableIpWhitelist,
  callEnableIpWhitelist,
  callUpdateIpWhitelistMemo,
  useIpWhitelistAuditLogs,
} from '../../shared/hooks/useIpWhitelists';
import { IpWhitelistRecord, SECURITY_AUDIT_ACTION_LABEL } from '../../shared/types/ipWhitelist';
import { ModalOverlay } from '../../shared/components/ModalOverlay';

export function IpWhitelistPage() {
  const { hasRole, isLoading: authLoading } = useAuthorization();
  const isSystemAdmin = hasRole('system_admin');

  const { items, isLoading, error, refetch } = useIpWhitelists(true);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editTarget, setEditTarget] = useState<IpWhitelistRecord | null>(null);
  const [auditTarget, setAuditTarget] = useState<IpWhitelistRecord | null | 'all'>(null);

  const activeCount = useMemo(() => items.filter(i => i.status === 'active').length, [items]);

  if (authLoading) return <div style={{ padding: 24 }}>로딩 중...</div>;
  if (!isSystemAdmin) {
    return (
      <div style={{ padding: 24, color: 'var(--color-red-600)' }}>
        접근 권한이 없습니다. system_admin 만 이용 가능합니다.
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <Shield size={20} />
        <h1 style={{ margin: 0, fontSize: 18 }}>IP 화이트리스트 관리</h1>
        <span
          style={{
            fontSize: 12,
            padding: '2px 8px',
            background: 'var(--color-gray-100)',
            borderRadius: 12,
            color: 'var(--color-gray-700)',
          }}
        >
          active {activeCount}건 / 전체 {items.length}건
        </span>
      </div>

      <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
        <button
          onClick={() => setShowAddModal(true)}
          style={{
            padding: '6px 12px',
            background: 'var(--color-blue-600)',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
          }}
        >
          <Plus size={14} /> IP 추가
        </button>
        <button
          onClick={() => setAuditTarget('all')}
          style={{
            padding: '6px 12px',
            background: 'white',
            border: '1px solid var(--color-gray-300)',
            borderRadius: 4,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
          }}
        >
          <History size={14} /> 전체 감사로그
        </button>
        <button
          onClick={() => refetch()}
          style={{
            padding: '6px 12px',
            background: 'white',
            border: '1px solid var(--color-gray-300)',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          새로고침
        </button>
      </div>

      {activeCount <= 1 && (
        <div
          style={{
            background: 'var(--color-orange-50)',
            color: 'var(--color-orange-700)',
            padding: 12,
            borderRadius: 4,
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          ⚠ 현재 active IP 가 {activeCount} 건입니다. 마지막 1건을 비활성화하면 운영 잠김 위험이 있습니다.
          (env emergency fallback 으로 차단은 막을 수 있으나 권장하지 않음)
        </div>
      )}

      {isLoading ? (
        <div>조회 중...</div>
      ) : error ? (
        <div style={{ color: 'var(--color-red-600)' }}>{error.message}</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--color-gray-50)', borderBottom: '2px solid var(--color-gray-200)' }}>
              <Th>상태</Th>
              <Th>IP</Th>
              <Th>type</Th>
              <Th>CIDR</Th>
              <Th>라벨</Th>
              <Th>메모</Th>
              <Th>등록</Th>
              <Th>변경</Th>
              <Th align="right">액션</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                <Td>
                  <span
                    style={{
                      fontSize: 11,
                      padding: '2px 8px',
                      borderRadius: 10,
                      background: it.status === 'active' ? 'var(--color-green-100)' : 'var(--color-gray-100)',
                      color: it.status === 'active' ? 'var(--color-green-700)' : 'var(--color-gray-600)',
                    }}
                  >
                    {it.status}
                  </span>
                </Td>
                <Td>
                  <span style={{ fontFamily: 'monospace' }}>{it.ip}</span>
                </Td>
                <Td>{it.type}</Td>
                <Td>{it.cidr || '-'}</Td>
                <Td>{it.label || '-'}</Td>
                <Td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.memo || ''}>
                  {it.memo || '-'}
                </Td>
                <Td style={{ fontSize: 11, color: 'var(--color-gray-600)' }}>
                  {it.createdByName || it.createdBy?.slice(0, 6)}
                  <br />
                  {(it.createdAt || '').slice(0, 16).replace('T', ' ')}
                </Td>
                <Td style={{ fontSize: 11, color: 'var(--color-gray-600)' }}>
                  {(it.updatedAt || '').slice(0, 16).replace('T', ' ')}
                </Td>
                <Td align="right">
                  <button
                    onClick={() => setEditTarget(it)}
                    title="메모 수정"
                    style={iconBtnStyle}
                  >
                    <Edit2 size={13} />
                  </button>
                  {it.status === 'active' ? (
                    <button
                      onClick={async () => {
                        const isLast = activeCount <= 1;
                        if (isLast) {
                          if (!window.confirm('마지막 active IP 입니다. 비활성화 시 운영 잠김 위험. 강제 진행하시겠습니까?')) return;
                        } else {
                          if (!window.confirm(`${it.ip} 을(를) 비활성화하시겠습니까?`)) return;
                        }
                        const reason = window.prompt('사유 (감사로그용, 선택)') || undefined;
                        try {
                          await callDisableIpWhitelist({ id: it.id, reason, confirmEvenIfLast: isLast });
                          await refetch();
                        } catch (e: any) {
                          alert('비활성화 실패: ' + (e.message || e));
                        }
                      }}
                      title="비활성화"
                      style={{ ...iconBtnStyle, color: 'var(--color-red-600)' }}
                    >
                      <Power size={13} />
                    </button>
                  ) : (
                    <button
                      onClick={async () => {
                        if (!window.confirm(`${it.ip} 을(를) 재활성화하시겠습니까?`)) return;
                        const reason = window.prompt('사유 (감사로그용, 선택)') || undefined;
                        try {
                          await callEnableIpWhitelist({ id: it.id, reason });
                          await refetch();
                        } catch (e: any) {
                          alert('재활성화 실패: ' + (e.message || e));
                        }
                      }}
                      title="재활성화"
                      style={{ ...iconBtnStyle, color: 'var(--color-green-700)' }}
                    >
                      <RotateCcw size={13} />
                    </button>
                  )}
                  <button onClick={() => setAuditTarget(it)} title="감사로그" style={iconBtnStyle}>
                    <History size={13} />
                  </button>
                </Td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><Td>등록된 IP 가 없습니다.</Td></tr>
            )}
          </tbody>
        </table>
      )}

      {showAddModal && (
        <AddModal
          onClose={() => setShowAddModal(false)}
          onAdded={async () => {
            setShowAddModal(false);
            await refetch();
          }}
        />
      )}
      {editTarget && (
        <EditMemoModal
          target={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={async () => {
            setEditTarget(null);
            await refetch();
          }}
        />
      )}
      {auditTarget && (
        <AuditModal
          target={auditTarget === 'all' ? undefined : auditTarget}
          onClose={() => setAuditTarget(null)}
        />
      )}
    </div>
  );
}

const iconBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--color-gray-300)',
  borderRadius: 4,
  padding: '4px 6px',
  marginLeft: 4,
  cursor: 'pointer',
};

function Th({ children, align }: { children: React.ReactNode; align?: 'left' | 'right' | 'center' }) {
  return (
    <th style={{ padding: '8px 8px', textAlign: align || 'left', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {children}
    </th>
  );
}
function Td({ children, align, style, title }: { children: React.ReactNode; align?: 'left' | 'right' | 'center'; style?: React.CSSProperties; title?: string }) {
  return (
    <td title={title} style={{ padding: '8px 8px', textAlign: align || 'left', verticalAlign: 'middle', ...style }}>
      {children}
    </td>
  );
}

function AddModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => Promise<void> }) {
  const [ip, setIp] = useState('');
  const [type, setType] = useState<'single' | 'cidr'>('single');
  const [cidr, setCidr] = useState('');
  const [label, setLabel] = useState('');
  const [memo, setMemo] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>('');

  const submit = async () => {
    setBusy(true);
    setErr('');
    try {
      await callCreateIpWhitelist({
        ip,
        type,
        cidr: type === 'cidr' ? cidr : undefined,
        label: label || undefined,
        memo: memo || undefined,
      });
      await onAdded();
    } catch (e: any) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose} style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={modalStyle}>
        <Header title="IP 추가" onClose={onClose} />
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Field label="IP (IPv4)">
            <input value={ip} onChange={(e) => setIp(e.target.value)} placeholder="59.13.192.250" style={inputStyle} />
          </Field>
          <Field label="type">
            <select value={type} onChange={(e) => setType(e.target.value as any)} style={inputStyle}>
              <option value="single">single</option>
              <option value="cidr">cidr</option>
            </select>
          </Field>
          {type === 'cidr' && (
            <Field label="CIDR">
              <input value={cidr} onChange={(e) => setCidr(e.target.value)} placeholder="59.13.192.0/24" style={inputStyle} />
            </Field>
          )}
          <Field label="라벨">
            <input value={label} onChange={(e) => setLabel(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="메모">
            <input value={memo} onChange={(e) => setMemo(e.target.value)} style={inputStyle} />
          </Field>
          {err && <div style={{ color: 'var(--color-red-600)', fontSize: 12 }}>{err}</div>}
        </div>
        <Footer>
          <button onClick={onClose} disabled={busy} style={btnSecondary}>취소</button>
          <button onClick={submit} disabled={busy || !ip} style={btnPrimary}>{busy ? '저장 중...' : '저장'}</button>
        </Footer>
      </div>
    </ModalOverlay>
  );
}

function EditMemoModal({ target, onClose, onSaved }: { target: IpWhitelistRecord; onClose: () => void; onSaved: () => Promise<void> }) {
  const [label, setLabel] = useState(target.label || '');
  const [memo, setMemo] = useState(target.memo || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>('');

  const submit = async () => {
    setBusy(true);
    setErr('');
    try {
      await callUpdateIpWhitelistMemo({ id: target.id, label, memo });
      await onSaved();
    } catch (e: any) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose} style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={modalStyle}>
        <Header title={`메모 수정: ${target.ip}`} onClose={onClose} />
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Field label="라벨">
            <input value={label} onChange={(e) => setLabel(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="메모">
            <input value={memo} onChange={(e) => setMemo(e.target.value)} style={inputStyle} />
          </Field>
          {err && <div style={{ color: 'var(--color-red-600)', fontSize: 12 }}>{err}</div>}
        </div>
        <Footer>
          <button onClick={onClose} disabled={busy} style={btnSecondary}>취소</button>
          <button onClick={submit} disabled={busy} style={btnPrimary}>{busy ? '저장 중...' : '저장'}</button>
        </Footer>
      </div>
    </ModalOverlay>
  );
}

function AuditModal({ target, onClose }: { target?: IpWhitelistRecord; onClose: () => void }) {
  const { items, isLoading, error } = useIpWhitelistAuditLogs(target?.id);
  return (
    <ModalOverlay onClose={onClose} style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...modalStyle, maxWidth: 800, maxHeight: '85vh' }}>
        <Header title={target ? `감사로그: ${target.ip}` : '전체 감사로그'} onClose={onClose} />
        <div style={{ padding: 16, overflow: 'auto', flex: 1 }}>
          {isLoading ? (
            <div>조회 중...</div>
          ) : error ? (
            <div style={{ color: 'var(--color-red-600)' }}>{error.message}</div>
          ) : items.length === 0 ? (
            <div style={{ color: 'var(--color-gray-500)' }}>로그 없음</div>
          ) : (
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--color-gray-50)' }}>
                  <Th>시각</Th>
                  <Th>action</Th>
                  <Th>actor</Th>
                  <Th>대상 IP</Th>
                  <Th>before/after</Th>
                  <Th>사유</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                    <Td>{(it.createdAt || '').slice(0, 19).replace('T', ' ')}</Td>
                    <Td>{SECURITY_AUDIT_ACTION_LABEL[it.action] || it.action}</Td>
                    <Td>{it.actorName || it.actorId.slice(0, 6)}</Td>
                    <Td style={{ fontFamily: 'monospace' }}>{it.targetIp}{it.targetCidr ? ` (${it.targetCidr})` : ''}</Td>
                    <Td style={{ fontSize: 11, color: 'var(--color-gray-700)' }}>
                      {it.before ? <div>before: {JSON.stringify(it.before)}</div> : null}
                      {it.after ? <div>after: {JSON.stringify(it.after)}</div> : null}
                    </Td>
                    <Td>{it.reason || '-'}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <Footer>
          <button onClick={onClose} style={btnSecondary}>닫기</button>
        </Footer>
      </div>
    </ModalOverlay>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
      <span style={{ color: 'var(--color-gray-700)' }}>{label}</span>
      {children}
    </label>
  );
}
function Header({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <h2 style={{ margin: 0, fontSize: 15 }}>{title}</h2>
      <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
        <X size={18} />
      </button>
    </div>
  );
}
function Footer({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: '10px 16px', borderTop: '1px solid var(--color-gray-200)', display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
      {children}
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: 8,
  width: '90vw',
  maxWidth: 600,
  maxHeight: '85vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
};
const inputStyle: React.CSSProperties = {
  padding: '6px 8px',
  border: '1px solid var(--color-gray-300)',
  borderRadius: 4,
  fontSize: 13,
};
const btnPrimary: React.CSSProperties = {
  padding: '6px 14px',
  background: 'var(--color-blue-600)',
  color: 'white',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 13,
};
const btnSecondary: React.CSSProperties = {
  padding: '6px 14px',
  background: 'white',
  color: 'var(--color-gray-700)',
  border: '1px solid var(--color-gray-300)',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 13,
};
